import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Badge, Box, Card, Group, Progress, Stack, Text, Title } from '@mantine/core';
import { IconUsersGroup } from '@tabler/icons-react';
import { groupsAPI } from '../../services/api';

const AVATAR_COLORS = ['blue', 'teal', 'grape', 'orange', 'cyan', 'pink'];
const colorFor = (name = '') => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

// Real budget/contribution data (group_contributions table, see
// backend/routes/groups.js) replaces the old dashboard's Math.random()
// placeholder progress bar.
const GroupGiftPanel = ({ groups }) => {
  const navigate = useNavigate();
  const [details, setDetails] = useState({}); // groupId -> { members, totalContributed }
  const shown = groups.slice(0, 3);

  useEffect(() => {
    let cancelled = false;
    shown.forEach(async (group) => {
      try {
        const [detailRes, contribRes] = await Promise.all([
          groupsAPI.getById(group.id),
          groupsAPI.getContributions(group.id)
        ]);
        if (cancelled) return;
        setDetails(prev => ({
          ...prev,
          [group.id]: {
            members: detailRes.data.members || [],
            totalContributed: contribRes.data.total_contributed || 0
          }
        }));
      } catch (err) {
        // Non-critical for the dashboard summary view; the group's own page
        // still works if this fails.
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  if (groups.length === 0) return null;

  return (
    <Box>
      <Title order={3} size="h5" mb="sm">Group gifts</Title>
      <Stack gap="sm">
        {shown.map((group) => {
          const detail = details[group.id];
          const progress = group.budget_limit && detail
            ? Math.min((detail.totalContributed / group.budget_limit) * 100, 100)
            : null;

          return (
            <Card
              key={group.id}
              p="md"
              radius="lg"
              withBorder
              onClick={() => navigate('/groups')}
              style={{ cursor: 'pointer' }}
            >
              <Group justify="space-between" mb={6} wrap="nowrap">
                <Text fw={600} size="sm" truncate>{group.name}</Text>
                <Badge size="xs" variant="light" color="teal" leftSection={<IconUsersGroup size={11} />}>
                  {group.member_count}
                </Badge>
              </Group>

              {detail?.members?.length > 0 && (
                <Group gap={0} mb={progress !== null ? 8 : 0}>
                  {detail.members.slice(0, 5).map((m) => (
                    <Avatar
                      key={m.id || m.user_id || m.email}
                      size={22}
                      radius="xl"
                      color={colorFor(m.name)}
                      variant="light"
                      style={{ marginLeft: -6, border: '2px solid var(--mantine-color-body)' }}
                    >
                      {(m.name || '?').charAt(0).toUpperCase()}
                    </Avatar>
                  ))}
                </Group>
              )}

              {progress !== null && (
                <>
                  <Progress
                    value={progress}
                    size="sm"
                    radius="xl"
                    color={progress >= 100 ? 'teal' : progress >= 60 ? 'blue' : 'gray'}
                  />
                  <Text size="xs" c="dimmed" mt={4}>
                    ${detail.totalContributed.toFixed(0)} of ${Number(group.budget_limit).toFixed(0)} collected
                  </Text>
                </>
              )}

              {!group.budget_limit && (
                <Text size="xs" c="dimmed">
                  {group.description || 'No budget set yet'}
                </Text>
              )}
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
};

export default GroupGiftPanel;
